import Icons from '@components/Icons';
import Text from '@components/Text';
import Colors from '@constants/Colors';
import { CircularProgress, IconButton, MenuItem, Select, Stack, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import usePagination from '@mui/material/usePagination';
import * as React from 'react';

export const DEFAULT_PAGE_SIZE = 20;

const List = styled('ul')({
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
});
type Props = {
    currentPage: number;
    data?: { offset: number; limit: number };
    hasNext: boolean;
    hasPrev: boolean;
    totalPages: number;
    loading: boolean;
    onChange: ({ limit, offset }: { limit: number; offset: number }) => void;
};

export default function Pagination(props: Props) {
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const isMobile = useMediaQuery('(max-width:600px)');
    const { items } = usePagination({
        count: props.totalPages,
        hideNextButton: !props.hasNext,
        hidePrevButton: !props.hasPrev,

        // siblingCount: 0,
        page: props.data?.offset ? Math.floor(props.data?.offset / props.data?.limit) + 1 : props.currentPage || 1,
        onChange: (_: React.ChangeEvent<unknown>, value: number) => {
            setCurrentPage(value);
            // offset should calculated from page number and limit
            props.onChange({ limit: props.data?.limit, offset: props.data?.limit * (value - 1) });
        },
    });

    return (
        <Stack
            flexWrap="wrap"
            direction={isMobile ? 'column' : 'row'}
            justifyContent={'center'}
            alignItems={'center'}
            flex={1}
            spacing={isMobile ? 1 : 2}>
            <nav style={{ maxWidth: '100%' }}>
                <List
                    style={{ gap: isMobile ? 4 : 8, flexWrap: 'wrap', paddingBottom: isMobile ? 8 : 0, width: '100%' }}>
                    {items.map(({ page, type, selected, ...item }, index) => {
                        let children = null;
                        if (type === 'start-ellipsis' || type === 'end-ellipsis') {
                            children = (
                                <div
                                    style={{
                                        width: isMobile ? 32 : 40,
                                        height: isMobile ? 32 : 40,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        fontSize: 14,
                                        fontWeight: 600,
                                    }}>
                                    <Text bold darkGray>
                                        ...
                                    </Text>
                                </div>
                            );
                        } else if (type === 'page') {
                            children = (
                                <IconButton
                                    type="button"
                                    disabled={selected}
                                    style={{
                                        backgroundColor: selected ? Colors.PRIMARY : undefined,
                                        borderRadius: 20,
                                        width: isMobile ? 32 : 40,
                                        height: isMobile ? 32 : 40,
                                        fontSize: 14,
                                        fontWeight: 600,
                                        color: selected ? Colors.WHITE : Colors.NEUTRAL_DARK_GRAY,
                                    }}
                                    {...item}>
                                    {props.loading && currentPage === page ? (
                                        <CircularProgress size={14} color={selected ? 'info' : 'primary'} />
                                    ) : (
                                        page
                                    )}
                                </IconButton>
                            );
                        } else if (type === 'previous') {
                            children = (
                                <IconButton
                                    type="button"
                                    disabled={!props.hasPrev}
                                    style={{
                                        backgroundColor: selected ? Colors.PRIMARY : undefined,
                                        borderRadius: 20,
                                        fontSize: 14,
                                        fontWeight: 600,
                                        width: isMobile ? 32 : 40,
                                        height: isMobile ? 32 : 40,
                                        color: selected ? Colors.WHITE : Colors.NEUTRAL_DARK_GRAY,
                                    }}
                                    {...item}>
                                    <Icons name="arrow-left" size={20} color={Colors.NEUTRAL_DARK_GRAY} />
                                </IconButton>
                            );
                        } else if (type === 'next') {
                            children = (
                                <IconButton
                                    type="button"
                                    disabled={!props.hasNext}
                                    style={{
                                        backgroundColor: Colors.NEUTRAL_LIGHT_GRAY,
                                        borderRadius: 20,
                                        padding: isMobile ? 8 : 12,
                                        fontSize: 14,
                                        width: isMobile ? 32 : 40,
                                        height: isMobile ? 32 : 40,
                                        fontWeight: 600,
                                        color: selected ? Colors.WHITE : Colors.NEUTRAL_DARK_GRAY,
                                    }}
                                    {...item}>
                                    <Icons name="arrow-right" size={16} color={Colors.NEUTRAL_DARK_GRAY} />
                                </IconButton>
                            );
                        } else {
                            return null;
                        }
                        return <li key={index}>{children}</li>;
                    })}
                </List>
            </nav>
            <Stack direction={'row'} alignItems="center" gap={1}>
                <Text
                    style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: Colors.NEUTRAL_DARK_GRAY,
                    }}>
                    Éléments par page:
                </Text>
                <Select
                    sx={{
                        height: 40,
                        fontSize: 14,
                        fontWeight: 600,
                        borderRadius: 20,
                    }}
                    value={props.data?.limit}
                    onChange={e => {
                        props.onChange({ limit: e.target.value as number, offset: 0 });
                    }}>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                </Select>
            </Stack>
        </Stack>
    );
}
